import type { CaseStudy } from '../types';

export const wework: CaseStudy = {
  id: 'wework',
  order: 5,
  title: 'WeWork',
  english: 'WeWork',
  category: 'finance',
  era: '2010 — 2023',
  region: '美国 / 全球',
  accent: '#a7782f',
  hook: '它把长期租约包装成短期灵活办公，再用科技公司的估值出售。期限错配本身不是骗局，但缺少缓冲的期限错配，会把一次需求下滑放大成生存危机。',
  question: '为什么一家“轻资产”的办公空间公司，最后被长期租约压垮？',
  summary:
    'WeWork 用长期租约锁定空间，再以短期会员制出售灵活性。低利率和扩张叙事掩盖了租约义务；当入住率、融资和办公需求同时转向，固定租金就变成了无法快速收缩的负债。',
  thesis: '“灵活”可以是产品，但不能自动消除资产负债表上的刚性；谁承担长期承诺，谁就承担周期。',
  quote: '你可以把办公室租得很灵活，但只要租约是长期的，风险就没有消失，只是换了一个科目。',
  entryNodeIds: ['wework-mismatch', 'wework-growth', 'wework-governance'],
  focusNodeId: 'wework-mismatch',
  lensIds: ['mismatch', 'scale', 'incentives', 'reflexivity'],
  opportunityIds: ['opp-flex-space'],
  sourceIds: ['reuters-wework-2023', 'wework-court'],
  facts: [
    {
      text: 'WeWork 2010 年成立于纽约，以长期租赁办公空间、再按工位或短租形式提供给会员为核心模式。',
      sourceIds: ['wework-court'],
    },
    {
      text: '2019 年 IPO 计划撤回，估值从私募市场高点大幅回落；软银随后提供救助。2023 年 11 月，WeWork 在美国申请破产保护。',
      sourceIds: ['reuters-wework-2023'],
    },
    {
      text: '破产文件显示，公司面对大量长期租约义务与相对短期的会员收入；具体债务与租约规模以法院文件为准。',
      sourceIds: ['wework-court'],
    },
  ],
  nodes: [
    {
      id: 'wework',
      caseId: 'wework',
      kind: 'case',
      stage: 0,
      lane: 0,
      title: 'WeWork',
      summary: '把长期租约切成短期工位，以增长故事融资。',
      detail:
        'WeWork 的产品是灵活办公：企业可以按月或更短周期租用工位，避免传统办公室的长租约。但它的成本端并不灵活——公司自己签下多年租约。这个错配在扩张期看似只是财务技巧，在需求下滑时却决定了生死。',
      boundary: '本案例聚焦商业模式与资产负债表，不覆盖所有共享办公企业；行业里也有更轻资产的运营模式。',
      evidence: 'fact',
      confidence: 'high',
      sourceIds: ['wework-court'],
    },
    {
      id: 'wework-mismatch',
      caseId: 'wework',
      kind: 'mechanism',
      stage: 1,
      lane: -1,
      title: '期限错配：短收入养长租约',
      summary: '资产端是多年租约，收入端却可以随时缩减，入住率一下降就被固定租金反噬。',
      detail:
        '长期租约锁定未来租金支出，短期会员制让收入可以快速流失。入住率高时，工位单价与租约成本之间的差价被放大；入住率下降时，空置成本几乎全部由公司承担。银行也会做期限转换，但银行有资本、存款保险和流动性工具；WeWork 没有同等缓冲。',
      boundary: '期限转换本身可以创造价值；关键是有没有足够的资本、流动性和可调整的租约条款来吸收冲击。',
      counter: '以管理合同、收入分成或短租物业运营的共享空间，可以把部分空置风险留给业主，错配程度更低。',
      evidence: 'mechanism',
      confidence: 'high',
      sourceIds: ['wework-court'],
    },
    {
      id: 'wework-growth',
      caseId: 'wework',
      kind: 'mechanism',
      stage: 1,
      lane: 1,
      title: '增长优先：用融资换规模',
      summary: '在低利率与风险资本支持下，先扩点位、再证明单位经济。',
      detail:
        '办公空间有本地网络效应：品牌、企业客户和点位密度可以互相导流。但每个新点位都意味着新的长期租约和装修资本开支。当扩张速度超过入住率与现金流改善，增长本身会消耗掉缓冲。',
      boundary: '如果每个新点位的回收期短、入住率稳定，增长优先可以成立。',
      evidence: 'mechanism',
      confidence: 'high',
      sourceIds: ['reuters-wework-2023'],
    },
    {
      id: 'wework-governance',
      caseId: 'wework',
      kind: 'mechanism',
      stage: 2,
      lane: -1,
      title: '治理与关联交易',
      summary: '创始人控制权、关联交易与薄弱董事会，让风险缺少外部刹车。',
      detail:
        '当创始人拥有特殊投票权、公司与创始人相关实体存在交易、董事会独立性不足时，扩张和估值叙事更难被挑战。治理不是形式，它决定了谁能在坏消息出现前说“不”。',
      boundary: '强控制权也能带来长期主义；问题在于是否配套透明的关联交易审查与独立监督。',
      evidence: 'inference',
      confidence: 'medium',
      sourceIds: ['wework-court'],
    },
    {
      id: 'wework-narrative',
      caseId: 'wework',
      kind: 'mechanism',
      stage: 2,
      lane: 1,
      title: '叙事溢价：用科技估值卖地产',
      summary: '把空间生意描述成科技平台，获得远高于传统地产的估值倍数。',
      detail:
        '如果投资者用科技公司的市销率而非地产公司的现金流倍数估值，同样的收入就能支撑更高估值。叙事并非全是假的：WeWork 确实有软件、社区和数据。但当估值主要由叙事驱动，融资就会变成维持增长的必要条件。',
      boundary: '叙事可以吸引资本和人才，但不能替代单位经济；一旦融资收紧，估值逻辑会迅速反转。',
      evidence: 'inference',
      confidence: 'high',
      sourceIds: ['reuters-wework-2023'],
    },
    {
      id: 'wework-condition',
      caseId: 'wework',
      kind: 'condition',
      stage: 2,
      lane: 0,
      title: '前提：低利率与灵活办公需求',
      summary: '廉价资本、 startup 繁荣与办公方式变化，共同支撑扩张叙事。',
      detail:
        '低利率降低长期租约的贴现成本，风险资本支持高速扩张，企业也愿意尝试灵活办公。疫情与远程办公改变了需求结构，但即使没有疫情，利率上升和供给过剩也会让错配承压。',
      boundary: '远程办公的冲击在不同城市、行业和租赁条款下差异很大，不能把破产全部归因于疫情。',
      evidence: 'inference',
      confidence: 'high',
      sourceIds: ['reuters-wework-2023'],
    },
    {
      id: 'wework-outcome',
      caseId: 'wework',
      kind: 'outcome',
      stage: 3,
      lane: 0,
      title: '结果：估值反转与破产重组',
      summary: 'IPO 撤回、融资收缩、入住率下滑，长期租约变成重组中的核心负担。',
      detail:
        'WeWork 没有因为“共享办公”这个想法本身而失败；失败的是在特定资本环境下，用长期刚性负债支撑短期收入的扩张方式。破产程序让它有机会拒绝或重谈部分租约，把成本重新拉回收入之下。',
      boundary: '破产不等于模式全盘无效；重组后的资产与运营仍可能继续。',
      evidence: 'fact',
      confidence: 'high',
      sourceIds: ['reuters-wework-2023', 'wework-court'],
    },
    {
      id: 'wework-risk',
      caseId: 'wework',
      kind: 'risk',
      stage: 4,
      lane: -1,
      title: '脆弱处：把“灵活”当成无风险',
      summary: '产品灵活，不等于成本灵活；风险只是从会员转到了公司的资产负债表。',
      detail:
        '当入住率下降，公司不能像会员一样“按月退出”。它必须继续支付租金、物业费和利息，或者通过破产重谈。对创业者，这是一个通用警告：不要用收入端的灵活性，去掩盖成本端的刚性。',
      boundary: '如果租约本身包含退出条款、短租或收入分成，成本刚性可以显著下降。',
      evidence: 'mechanism',
      confidence: 'high',
      sourceIds: ['wework-court'],
    },
    {
      id: 'wework-opp',
      caseId: 'wework',
      kind: 'opportunity',
      stage: 4,
      lane: 1,
      title: '机会透镜：先拿需求，再锁供给',
      summary: '轻资产灵活空间的机会，在于把租约风险留给愿意承担的业主，而不是自己吞下。',
      detail:
        '可行的方向包括：为业主做运营管理、按收入分成、利用闲置物业做短周期快闪、先验证企业需求再签长期租约。核心不是“共享办公”这个标签，而是把固定成本变成可变成本，并用数据提高入住率。',
      boundary: '轻资产模式毛利率更低、控制力更弱；没有稳定的企业客户和运营能力，只是把风险换了个位置。',
      evidence: 'hypothesis',
      confidence: 'medium',
      sourceIds: ['wework-court'],
    },
  ],
  edges: [
    {
      id: 'wework-e1',
      caseId: 'wework',
      from: 'wework',
      to: 'wework-mismatch',
      label: '长期租、短期卖',
      explanation: '公司签多年租约，会员却可以按月或更短周期退出，形成期限错配。',
      tone: 'cause',
    },
    {
      id: 'wework-e2',
      caseId: 'wework',
      from: 'wework',
      to: 'wework-growth',
      label: '资本推动扩张',
      explanation: '融资支持快速开点，每个新点又锁定新的长期租约。',
      tone: 'cause',
    },
    {
      id: 'wework-e3',
      caseId: 'wework',
      from: 'wework-mismatch',
      to: 'wework-outcome',
      label: '入住率决定生死',
      explanation: '收入下滑时，空置成本与固定租金同时压向公司，利润被迅速侵蚀。',
      tone: 'cause',
    },
    {
      id: 'wework-e4',
      caseId: 'wework',
      from: 'wework-growth',
      to: 'wework-outcome',
      label: '规模放大固定承诺',
      explanation: '点位越多，长期租约与装修投入越大，收缩越困难。',
      tone: 'cause',
    },
    {
      id: 'wework-e5',
      caseId: 'wework',
      from: 'wework-growth',
      to: 'wework-governance',
      label: '增长绕过刹车',
      explanation: '快速扩张与创始人控制，使关联交易和风险控制更难被独立审视。',
      tone: 'risk',
    },
    {
      id: 'wework-e6',
      caseId: 'wework',
      from: 'wework-narrative',
      to: 'wework-outcome',
      label: '估值依赖融资',
      explanation: '科技叙事支撑高估值，但一旦融资收紧，增长与估值同时反噬。',
      tone: 'cause',
    },
    {
      id: 'wework-e7',
      caseId: 'wework',
      from: 'wework-condition',
      to: 'wework-outcome',
      label: '环境反转',
      explanation: '低利率、灵活办公需求与风险资本同时转向，错配失去缓冲。',
      tone: 'enable',
    },
    {
      id: 'wework-e8',
      caseId: 'wework',
      from: 'wework-outcome',
      to: 'wework-risk',
      label: '风险显性化',
      explanation: '破产让长期租约的刚性被看见，所谓“轻资产”其实承担了重负债。',
      tone: 'risk',
    },
    {
      id: 'wework-e9',
      caseId: 'wework',
      from: 'wework-risk',
      to: 'wework-opp',
      label: '反向设计',
      explanation: '把租约风险留给业主、先验证需求，再决定是否锁定供给。',
      tone: 'enable',
    },
    {
      id: 'wework-e10',
      caseId: 'wework',
      from: 'wework-mismatch',
      to: 'wework-opp',
      label: '错配就是机会',
      explanation: '谁能设计更灵活的租约与运营结构，谁就能承接被刚性模式排除的需求。',
      tone: 'enable',
    },
  ],
};
